/**
 * RecentActivityWidget Component
 *
 * Dashboard widget displaying recent platform activity.
 * Shows recent bookings, guide signups, and reviews with timestamps.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-148 - Admin Dashboard (21 SP)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  ShoppingBag,
  UserPlus,
  Star as _Star,
  ArrowRight
} from 'lucide-react';
import StatusBadge from '../../common/StatusBadge';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { formatCentsToDollars } from '../../../utils/formHelpers';

export function RecentActivityWidget({ activity, loading }) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
        <LoadingSpinner message="Loading activity..." size="sm" />
      </div>
    );
  }

  if (!activity) {
    return null;
  }

  const formatActivityTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {return 'Just now';}
    if (diffMins < 60) {return `${diffMins}m ago`;}
    if (diffHours < 24) {return `${diffHours}h ago`;}
    if (diffDays < 7) {return `${diffDays}d ago`;}
    return date.toLocaleDateString();
  };

  const formatPrice = (cents) => {
    return formatCentsToDollars(cents);
  };

  // Combine and sort all activities by timestamp
  const allActivities = [
    ...(activity.recent_bookings || []).map(booking => ({
      type: 'booking',
      id: booking.id,
      timestamp: booking.created_at,
      title: booking.experience_title,
      subtitle: `${booking.customer_name} - ${formatPrice(booking.total_price_cents)}`,
      status: booking.status,
      icon: ShoppingBag,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      path: `/admin/bookings/${booking.id}`,
    })),
    ...(activity.recent_guides || []).map(guide => ({
      type: 'guide',
      id: guide.guide_id,
      timestamp: guide.created_at,
      title: guide.business_name,
      subtitle: `${guide.guide_name} - ${guide.status}`,
      status: guide.status,
      icon: UserPlus,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      path: `/admin/guide-approvals`,
    })),
    ...(activity.recent_reviews || []).map(review => ({
      type: 'review',
      id: review.review_id,
      timestamp: review.created_at,
      title: review.experience_title,
      subtitle: `${review.reviewer_name} - ${parseFloat(review.overall_rating).toFixed(1)} stars`,
      rating: parseFloat(review.overall_rating),
      icon: _Star,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      path: `/admin/reviews/${review.review_id}`,
    })),
  ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const getStatusVariant = (status) => {
    const variants = {
      confirmed: 'success',
      pending: 'warning',
      completed: 'success',
      cancelled: 'error',
    };
    return variants[status] || 'neutral';
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
        <button
          onClick={() => navigate('/admin/activity')}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
        >
          View all
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {allActivities.length === 0 ? (
        <EmptyState
          icon={Clock}
          title="No recent activity"
        />
      ) : (
        <div className="space-y-3">
          {allActivities.slice(0, 10).map((item, index) => (
            <button
              key={`${item.type}-${item.id}-${index}`}
              onClick={() => navigate(item.path)}
              className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <div className={`p-2 rounded-lg ${item.bgColor} flex-shrink-0`}>
                <item.icon className={`w-5 h-5 ${item.color}`} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {item.title}
                  </p>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {formatActivityTime(item.timestamp)}
                  </span>
                </div>
                <p className="text-xs text-gray-600 truncate mt-0.5">
                  {item.subtitle}
                </p>
                {item.status && (
                  <StatusBadge variant={getStatusVariant(item.status)}>
                    {item.status}
                  </StatusBadge>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default RecentActivityWidget;
